/**
 * **The command menu's height slide** (`projects/unit-panel.md` 1.5). The menu docks at the top of
 * the unit column and grows downward, so a screen change moves only its bottom edge; this makes that edge slide
 * instead of snap (the developer's "not feel jumpy").
 *
 * The element keeps its natural (`auto`) height, so the layout (the unit column's flex limits, the
 * rows' scroll) decides every size. After each render the hook reads where the element is now and
 * where `auto` puts it, and when they differ it pins the old height, then transitions to the new one
 * and lets go at the end. Read mid-slide, "now" is the transition's current value, so a change
 * during a slide carries on from where the edge is. Under `prefers-reduced-motion: reduce` it does
 * nothing: the height snaps.
 */
import { useLayoutEffect, useRef, type RefObject } from "react";

/** The slide's length and curve: a plain ease-out, since an overshoot in height reads as jitter. */
export const HEIGHT_SLIDE = "height 200ms ease-out";

/**
 * The slide to run, or `null` to leave the height alone: under reduced motion, and for a change of
 * under a pixel (sub-pixel layout noise).
 */
export function heightSlide(args: { current: number; target: number; reduced: boolean }): { from: number; to: number; transition: string } | null {
  const { current, target, reduced } = args;
  if (reduced || Math.abs(target - current) < 1) return null;
  return { from: current, to: target, transition: HEIGHT_SLIDE };
}

const prefersReducedMotion = (): boolean => typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Slide `ref`'s height between renders (see the module doc). Runs after every render of the caller. */
export function useAnimatedHeight(ref: RefObject<HTMLElement | null>): void {
  const sliding = useRef(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const current = el.getBoundingClientRect().height;
    el.style.transition = "none";
    el.style.height = "";
    const slide = heightSlide({ current, target: el.getBoundingClientRect().height, reduced: prefersReducedMotion() });
    if (!slide) {
      el.style.transition = "";
      sliding.current = false;
      return;
    }
    el.style.height = `${slide.from}px`;
    // Commit the start height before the transition applies, or the browser slides from `auto`.
    void el.offsetHeight;
    el.style.transition = slide.transition;
    el.style.height = `${slide.to}px`;
    sliding.current = true;
  });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const done = (e: TransitionEvent) => {
      if (e.target !== el || e.propertyName !== "height" || !sliding.current) return;
      sliding.current = false;
      el.style.transition = "";
      el.style.height = "";
    };
    // Not `transitioncancel`: a render mid-slide cancels the running slide to start the next one,
    // and that event arrives after the next one has begun.
    el.addEventListener("transitionend", done);
    return () => el.removeEventListener("transitionend", done);
  }, [ref]);
}
